import React from "react";
import { toast } from "sonner";
import { FileText } from "lucide-react";
import { Button } from "../../ui/button";
import { Card, CardContent } from "../../ui/card";
import { Headline, Kpi, PainBanner, Pill, SectionTitle } from "./PersonaUi";
import { OBS, REAL, SHORT } from "./allocatorData";

const CHECKS = ["Active share ≥ 80%", "Positioning grade ≥ C", "Crowded share < 12%", "No style drift", "Turnover normal"];
const SAMPLE = {
  OAKMX: { drift: true, turn: false, note: "Value fund drifting into growth names (sample); grade fell to F." },
  DODGX: { drift: false, turn: false, note: "Truly active (90%) but positioned away from where specialists converge." },
  FBGRX: { drift: false, turn: true, note: "Active share slipped under 80% on rolling basis; turnover spike in Q2 (sample)." },
};

const status = (r) => {
  const fails = r.checks.filter((c) => c === false).length;
  return fails >= 2 ? ["REVIEW", "bad"] : fails === 1 ? ["WATCH", "alert"] : ["ON TRACK", "good"];
};

export default function AllocMonitor() {
  const rows = REAL.lineup.map((f) => {
    const g = REAL.grades[f];
    const s = SAMPLE[f] || {};
    const checks = [g.active_share >= 0.8, g.letter ? !["D", "F"].includes(g.letter) : null, (g.crowded_share || 0) < 0.12, !s.drift, !s.turn];
    return { f, g, s, checks };
  }).map((r) => ({ ...r, st: status(r) }))
    .sort((a, b) => ["REVIEW", "WATCH", "ON TRACK"].indexOf(a.st[0]) - ["REVIEW", "WATCH", "ON TRACK"].indexOf(b.st[0]));
  const n = (k) => rows.filter((r) => r.st[0] === k).length;
  return (
    <div className="space-y-4" data-testid="persona-alloc-monitor">
      <PainBanner testid="persona-alloc-monitor-pain" persona="LONG-ONLY ALLOCATOR · WATCH-LIST MONITOR"
        pain="As a fiduciary I must monitor every fund each quarter and document why it stays on the menu."
        answer="pass/fail checks per fund + an investment-committee memo" />
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Headline testid="alloc-mon-headline">{n("REVIEW")} funds to review · {n("WATCH")} on watch · {n("ON TRACK")} on track</Headline>
          <div className="text-sm text-muted-foreground mt-1">Core US Equity menu · quarterly review · data as of {OBS}</div>
        </div>
        <Button size="sm" onClick={() => toast("Mockup — IC memo export not wired yet")} data-testid="alloc-mon-export-btn"><FileText className="w-4 h-4 mr-1" />Generate IC memo (PDF)</Button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi testid="alloc-mon-kpi-review" label="Review" value={n("REVIEW")} tone="text-red-400" sub="≥ 2 checks failed" />
        <Kpi testid="alloc-mon-kpi-watch" label="Watch" value={n("WATCH")} tone="text-amber-400" sub="1 check failed" />
        <Kpi testid="alloc-mon-kpi-index" label="Index-like (fee review)" value={rows.filter((r) => r.g.active_share < 0.8).length} sub="active share < 80%" />
        <Kpi testid="alloc-mon-kpi-docs" label="Documentation" value="Ready" tone="text-emerald-400" sub="every check timestamped" />
      </div>
      <Card><CardContent className="pt-4 overflow-x-auto">
        <table className="w-full text-xs" data-testid="alloc-mon-table">
          <thead className="border-b border-border text-muted-foreground"><tr>
            <th className="text-left py-1.5 px-2">Fund</th>
            {CHECKS.map((c) => <th key={c} className="text-center px-2">{c}</th>)}
            <th className="text-right px-2">Grade</th><th className="text-right px-2">Status</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.f} className="border-b border-border/40 align-top" data-testid={`alloc-mon-row-${r.f}`}>
                <td className="py-2 px-2"><span className="font-mono font-bold">{r.f}</span> <span className="text-[10px] text-muted-foreground">{SHORT[r.f]}</span>
                  {r.s.note && <div className="text-[10px] text-muted-foreground mt-0.5 max-w-xs">{r.s.note}</div>}</td>
                {r.checks.map((ok, i) => (
                  <td key={i} className={`text-center px-2 font-mono ${ok === null ? "text-muted-foreground" : ok ? "text-emerald-400" : "text-red-400"}`}>
                    {ok === null ? "n/a" : ok ? "✓" : "✗"}{i === 0 && <div className="text-[9px] text-muted-foreground">{(r.g.active_share * 100).toFixed(1)}%</div>}
                    {i === 2 && <div className="text-[9px] text-muted-foreground">{((r.g.crowded_share || 0) * 100).toFixed(1)}%</div>}
                  </td>
                ))}
                <td className="text-right px-2 font-mono">{r.g.letter || (r.g.ungraded_reason === "index_like" ? "n/a" : "—")}</td>
                <td className="text-right px-2"><Pill tone={r.st[1]}>{r.st[0]}</Pill></td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent></Card>
      <Card data-testid="alloc-mon-memo"><CardContent className="pt-4">
        <SectionTitle right={<Pill tone="info">IC MEMO PREVIEW</Pill>}>Investment committee — quarterly monitoring note</SectionTitle>
        <div className="text-[13px] leading-relaxed space-y-2 text-foreground/85">
          <p>All {rows.length} funds were screened against five pre-defined monitoring criteria using holdings filed with the SEC (N-PORT). {n("REVIEW")} funds failed two or more criteria and are recommended for formal review; {n("WATCH")} remain on watch.</p>
          <p>{rows.filter((r) => r.g.active_share < 0.8).length} funds currently hold portfolios with less than 80% active share versus their closest benchmark; the committee should confirm their fees remain justified relative to lower-cost index alternatives.</p>
          <p className="text-[11px] text-muted-foreground">Criteria, thresholds and data dates are recorded for audit. This note documents a monitoring process; it is not investment advice.</p>
        </div>
      </CardContent></Card>
      <div className="text-[10px] text-muted-foreground">Real: active share, grade, crowded share ({OBS}). Sample: style-drift and turnover flags and the fund notes.</div>
    </div>
  );
}
